import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { INameValue } from 'src/app/common/interfaces/name-value-interface';
import { ITableServerFilter } from 'src/app/common/interfaces/table/table-server-filter.inteface';
import { ITableServerResponseFilter } from 'src/app/common/interfaces/table/table-server-filter-response.interface';
import { environment } from 'src/environments/environment';
import { AbstractRejectAnalysisService } from '../abstraction/abstract.reject-analysis.service';
import { IRejectAnalysisTransactionDetailsRequest } from '../../interfaces/reject-analysis/reject-analysis-transaction-details-request.interface';

interface IRejectCodeApiResponse {
  key: string;
  value: string;
}

interface IRespondentApiResponse {
  participantRtn: string;
  participantName: string;
}

@Injectable({
  providedIn: 'root',
})
export class RejectAnalysisService extends AbstractRejectAnalysisService {
  private controller = `/manager/rejectanalysis`;
  private urlGrid = `${environment.apiUrl}${this.controller}/grid`;
  private urlGridExportCSV = `${environment.apiUrl}${this.controller}/gridExportCSV`;
  private urlGridExportExcel = `${environment.apiUrl}${this.controller}/gridExportExcel`;
  private urlRejectCodes = `${environment.apiUrl}${this.controller}/rejectcodes`;
  private urlRespondents = `${environment.apiUrl}${this.controller}/getRespondentList`;
  private urlTransactionDetails = `${environment.apiUrl}${this.controller}/getTransactionList`;
  private urlGetTransactionDetailGrid = `${environment.apiUrl}${this.controller}/transactionDetail`;
  private urlGetReturnTransactionDetailGrid = `${environment.apiUrl}${this.controller}/transactionReturnDetail`;

  private detailRowsCache = new Map<string, any[]>();
  private loadedRejectCodeOptions: INameValue[] = [];

  private formatRejectCodeOption(code: string, description: string): INameValue {
    const fullLabel = `${code} - ${description}`;
    return {
      name: code,
      value: fullLabel.length > 25 ? fullLabel.substring(0, 40) + '...' : fullLabel,
    };
  }

  private formatRespondentDisplayName(participantName: string, respondentRtn?: string): string {
    if (!respondentRtn) {
      return participantName;
    }

    return `${participantName} (${respondentRtn})`;
  }

  constructor(private http: HttpClient) {
    super();
  }

  tableFilter(filter: ITableServerFilter): Promise<ITableServerResponseFilter | undefined> {
    return this.http
      .post<any>(this.urlGrid, filter)
      .toPromise()
      .then(response => {
        if (!response) return response;

        // Validation error response: { status: "Error", message: "...", category: null }
        // Return a marker object so overwriteDataEvent fires and the component shows
        // the message in-page (like send-payment) rather than as a toast.
        if (response.status === 'Error' && response.message) {
          return {
            currentPage: 1,
            pageCount: 0,
            pageSize: 10,
            rowCount: 0,
            queryable: [],
            validationError: response.message,
          } as any;
        }

        this.detailRowsCache.clear();
        const summaryRows: any[] = [];
        const uniqueRejectCodes = new Map<string, string>();

        response.queryable.forEach((row: any) => {
          if (row.rowType === 'SUMMARY') {
            summaryRows.push({
              ...row,
              participantName: this.formatRespondentDisplayName(
                row.participantName,
                row.respondentRtn
              ),
              totalReceived: row.totalCTReceived,
              totalReject: row.receivedCTRejected,
              rejectPercent: `${Number(row.receiverRejectionPercent ?? 0).toFixed(2)}%`,
              dataKey: row.respondentRtn,
            });
          } else if (row.rowType === 'DETAIL') {
            const key = row.respondentRtn;
            if (!this.detailRowsCache.has(key)) {
              this.detailRowsCache.set(key, []);
            }
            this.detailRowsCache.get(key)!.push({
              ...row,
              dataKey: key,
              codeCount: row.totalRejectCodeCount,
              codePercent: `${Number(row.totalRejectCodePercent ?? 0).toFixed(2)}%`,
            });
            if (row.rejectCode && !uniqueRejectCodes.has(row.rejectCode)) {
              uniqueRejectCodes.set(row.rejectCode, row.rejectCodeDescription ?? '');
            }
          }
        });

        this.loadedRejectCodeOptions = [
          { name: 'All', value: 'All' },
          ...Array.from(uniqueRejectCodes.entries())
            .sort(([codeA], [codeB]) => codeA.localeCompare(codeB))
            .map(([code, description]) => this.formatRejectCodeOption(code, description)),
        ];

        return { ...response, queryable: summaryRows };
      }) as Promise<ITableServerResponseFilter | undefined>;
  }

  tableExportCSV(filter: ITableServerFilter): Promise<Blob | undefined> {
    return this.http.post(this.urlGridExportCSV, filter, { responseType: 'blob' }).toPromise();
  }

  tableExportExcel(filter: ITableServerFilter): Promise<any | undefined> {
    return this.http.post(this.urlGridExportExcel, filter).toPromise();
  }

  getDetailsForRow(dataKey: string): Promise<any[]> {
    return Promise.resolve(this.detailRowsCache.get(dataKey) ?? []);
  }

  getTransactionDetails(
    request: IRejectAnalysisTransactionDetailsRequest
  ): Promise<ITableServerResponseFilter | undefined> {
    const options = {
      headers: new HttpHeaders({ 'Skip-Loading': 'true' }),
    };

    const apiRequest = {
      startdate: request.startDate,
      enddate: request.endDate,
      correspondentRtn: request.correspondentRtn,
      respondentRtn: request.respondentRtn,
      FreeText: request.freeText,
      RejectCode: request.rejectCode,
      CurrentPage: request.currentPage,
      PageSize: request.pageSize,
      sortBy: request.sortBy,
      sortAsc: request.sortAsc,
    };

    return this.http
      .post<any>(this.urlTransactionDetails, apiRequest, options)
      .toPromise()
      .then(response => this.normalizeTransactionDetailsResponse(response, request))
      .catch((error: HttpErrorResponse) => {
        if (error?.status !== 405) {
          throw error;
        }

        const params = new HttpParams()
          .set('startdate', request.startDate ?? '')
          .set('enddate', request.endDate ?? '')
          .set('correspondentRtn', request.correspondentRtn ?? '')
          .set('respondentRtn', request.respondentRtn ?? '')
          .set('FreeText', request.freeText ?? '')
          .set('RejectCode', request.rejectCode ?? '')
          .set('CurrentPage', String(request.currentPage ?? 1))
          .set('PageSize', String(request.pageSize ?? 10))
          .set('sortBy', request.sortBy ?? '')
          .set('sortAsc', String(request.sortAsc ?? false));

        return this.http
          .get<any>(this.urlTransactionDetails, { ...options, params })
          .toPromise()
          .then(response => this.normalizeTransactionDetailsResponse(response, request));
      }) as Promise<ITableServerResponseFilter | undefined>;
  }

  private normalizeTransactionDetailsResponse(
    response: any,
    request: IRejectAnalysisTransactionDetailsRequest
  ): ITableServerResponseFilter {
    if (Array.isArray(response)) {
      const pageSize = Number(request.pageSize ?? response.length ?? 10) || 10;
      return {
        currentPage: Number(request.currentPage ?? 1) || 1,
        pageCount: response.length > 0 ? Math.ceil(response.length / pageSize) : 0,
        pageSize,
        rowCount: response.length,
        queryable: response,
      };
    }

    if (Array.isArray(response?.queryable)) {
      return {
        currentPage: Number(response.currentPage ?? request.currentPage ?? 1) || 1,
        pageCount: Number(response.pageCount ?? 0) || 0,
        pageSize: Number(response.pageSize ?? request.pageSize ?? 10) || 10,
        rowCount: Number(response.rowCount ?? response.queryable.length ?? 0) || 0,
        queryable: response.queryable,
      };
    }

    return {
      currentPage: Number(request.currentPage ?? 1) || 1,
      pageCount: 0,
      pageSize: Number(request.pageSize ?? 10) || 10,
      rowCount: 0,
      queryable: [],
    };
  }

  getById(messageId: string, type: string, primaryRtn: string): Promise<any> {
    return this.http
      .post<any>(this.urlGetTransactionDetailGrid, {
        MessageId: messageId,
        TransactionType: type,
        PrimaryRtn: primaryRtn,
      })
      .toPromise();
  }

  getReturnById(messageId: string, type: string, primaryRtn: string): Promise<any> {
    return this.http
      .post<any>(this.urlGetReturnTransactionDetailGrid, {
        MessageId: messageId,
        TransactionType: type,
        PrimaryRtn: primaryRtn,
      })
      .toPromise();
  }

  getRejectCodeOptions(): Promise<INameValue[]> {
    return this.http
      .get<IRejectCodeApiResponse[]>(this.urlRejectCodes)
      .toPromise()
      .then(response => {
        const options: INameValue[] = [{ name: 'All', value: 'All' }];
        (response ?? []).forEach(item => {
          options.push(this.formatRejectCodeOption(item.key, item.value));
        });
        return options;
      }) as Promise<INameValue[]>;
  }

  getLoadedRejectCodeOptions(): INameValue[] {
    return this.loadedRejectCodeOptions;
  }

  getRespondentOptions(): Promise<INameValue[]> {
    return this.http
      .get<IRespondentApiResponse[]>(this.urlRespondents)
      .toPromise()
      .then(response => {
        const options: INameValue[] = [{ name: 'All', value: 'All' }];
        (response ?? []).forEach(item => {
          const label = `${item.participantRtn} - ${item.participantName}`;
          options.push({
            name: item.participantRtn,
            value: label.length > 30 ? label.substring(0, 30) + '...' : label,
          });
        });
        return options;
      }) as Promise<INameValue[]>;
  }
}
